/**
 * FERP Portal - Field Renderer
 *
 * Renders individual form fields based on DSL FieldDefinition.
 * Uses Mantine v8.x components exclusively.
 *
 * CONSTRAINTS:
 * - NO MUI components
 * - All field types must be supported
 * - Validation must be real-time
 */

import { memo, useMemo } from 'react';
import {
  TextInput,
  NumberInput,
  PasswordInput,
  Textarea,
  Select,
  MultiSelect,
  Checkbox,
  Radio,
  Switch,
  Slider,
  Rating,
  ColorInput,
  FileInput,
  JsonInput,
  Box,
  Text,
  Group,
  Stack,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { DateInput, DateTimePicker, TimeInput } from '@mantine/dates';
import { IconInfoCircle, IconUpload } from '@tabler/icons-react';
import type { FieldDefinition, FieldRendererProps, OptionItem } from './types';

// ============================================================================
// FIELD COMPONENT REGISTRY
// ============================================================================

type FieldComponent = React.ComponentType<FieldRendererProps>;

/**
 * Registry of field components by type
 */
const fieldComponents: Record<string, FieldComponent> = {
  text: TextFieldRenderer,
  number: NumberFieldRenderer,
  email: EmailFieldRenderer,
  password: PasswordFieldRenderer,
  tel: PhoneFieldRenderer,
  url: UrlFieldRenderer,
  date: DateFieldRenderer,
  datetime: DateTimeFieldRenderer,
  time: TimeFieldRenderer,
  select: SelectFieldRenderer,
  multiselect: MultiSelectFieldRenderer,
  checkbox: CheckboxFieldRenderer,
  radio: RadioFieldRenderer,
  switch: SwitchFieldRenderer,
  textarea: TextareaFieldRenderer,
  json: JsonFieldRenderer,
  code: CodeFieldRenderer,
  file: FileFieldRenderer,
  image: ImageFieldRenderer,
  color: ColorFieldRenderer,
  range: RangeFieldRenderer,
  rating: RatingFieldRenderer,
  signature: SignatureFieldRenderer,
  'rich-text': RichTextFieldRenderer,
  address: AddressFieldRenderer,
  phone: PhoneFieldRenderer,
  currency: CurrencyFieldRenderer,
  custom: CustomFieldRenderer,
};

// ============================================================================
// MAIN FIELD RENDERER
// ============================================================================

/**
 * Main field renderer component
 * Routes to specific field type renderer
 */
export const FieldRenderer = memo(function FieldRenderer(props: FieldRendererProps) {
  const { field, context } = props;

  // Check visibility conditions
  const isVisible = useMemo(() => {
    if (!field.visibility) return true;

    const { showWhen, hideWhen } = field.visibility;

    if (hideWhen && context.evaluateCondition(hideWhen)) {
      return false;
    }

    if (showWhen && !context.evaluateCondition(showWhen)) {
      return false;
    }

    return true;
  }, [field.visibility, context]);

  // Check disabled/readonly state
  const isDisabled = useMemo(() => {
    if (props.disabled) return true;
    if (field.visibility?.disableWhen) {
      return context.evaluateCondition(field.visibility.disableWhen);
    }
    return false;
  }, [props.disabled, field.visibility, context]);

  const isReadOnly = useMemo(() => {
    if (props.readOnly) return true;
    if (field.visibility?.readOnlyWhen) {
      return context.evaluateCondition(field.visibility.readOnlyWhen);
    }
    return false;
  }, [props.readOnly, field.visibility, context]);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Get the appropriate field component
  const FieldComponent = fieldComponents[field.type] || CustomFieldRenderer;

  return (
    <FieldComponent
      {...props}
      disabled={isDisabled}
      readOnly={isReadOnly}
    />
  );
});

// ============================================================================
// FIELD WRAPPER
// ============================================================================

interface FieldWrapperProps {
  field: FieldDefinition;
  error?: string;
  children: React.ReactNode;
}

/**
 * Common wrapper for all field types
 * Provides label, description, help text, and error display
 */
function FieldWrapper({ field, error, children }: FieldWrapperProps) {
  return (
    <Box
      style={{
        width: getFieldWidth(field.width),
        gridColumn: field.gridColumn,
        gridRow: field.gridRow,
      }}
    >
      {children}
      {field.helpText && !error && (
        <Text size="xs" c="dimmed" mt={4}>
          {field.helpText}
        </Text>
      )}
    </Box>
  );
}

/**
 * Get CSS width for field width setting
 */
function getFieldWidth(width?: FieldDefinition['width']): string {
  switch (width) {
    case 'full':
      return '100%';
    case 'half':
      return '50%';
    case 'third':
      return '33.33%';
    case 'quarter':
      return '25%';
    case 'auto':
    default:
      return 'auto';
  }
}

/**
 * Convert OptionItem[] to Mantine select data format
 */
type SelectItem = { value: string; label: string; disabled?: boolean };
type SelectGroup = { group: string; items: SelectItem[] };

function toSelectData(options?: OptionItem[]): (SelectItem | SelectGroup)[] {
  if (!options) return [];

  // Check if any options use grouping
  const hasGroups = options.some((opt) => opt.group != null);

  if (!hasGroups) {
    // Simple items — do NOT include `group` key (Mantine's parseItem
    // treats any object with a `group` key as a group header and expects `items`)
    return options.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
      ...(opt.disabled ? { disabled: true } : {}),
    }));
  }

  // Convert per-item `group` into Mantine's { group, items } format
  const groupMap = new Map<string, SelectItem[]>();
  const ungrouped: SelectItem[] = [];

  for (const opt of options) {
    const item: SelectItem = { value: String(opt.value), label: opt.label };
    if (opt.disabled) item.disabled = true;

    if (opt.group) {
      let arr = groupMap.get(opt.group);
      if (!arr) {
        arr = [];
        groupMap.set(opt.group, arr);
      }
      arr.push(item);
    } else {
      ungrouped.push(item);
    }
  }

  const result: (SelectItem | SelectGroup)[] = [...ungrouped];
  for (const [group, items] of groupMap) {
    result.push({ group, items });
  }
  return result;
}

/**
 * Label with optional help icon
 */
function FieldLabel({ field }: { field: FieldDefinition }) {
  if (!field.description) {
    return <>{field.label}</>;
  }

  return (
    <Group gap={4}>
      <span>{field.label}</span>
      <Tooltip label={field.description} withArrow>
        <ActionIcon size="xs" variant="subtle" color="gray">
          <IconInfoCircle size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

// ============================================================================
// TEXT FIELDS
// ============================================================================

function TextFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <TextInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        maxLength={field.validation?.maxLength}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        leftSection={field.icon ? <span>{field.icon}</span> : undefined}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function EmailFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <TextInput
        type="email"
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'email@example.com'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function PasswordFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <PasswordInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function PhoneFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <TextInput
        type="tel"
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || '(555) 123-4567'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function UrlFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <TextInput
        type="url"
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'https://example.com'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

// ============================================================================
// NUMBER FIELDS
// ============================================================================

function NumberFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <NumberInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder}
        value={(value as number) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        min={field.validation?.min}
        max={field.validation?.max}
        step={field.validation?.step}
        decimalScale={field.validation?.precision}
        onChange={(val) => onChange(val)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function CurrencyFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  const currencySymbol = (field.props?.currencySymbol as string) || '$';

  return (
    <FieldWrapper field={field} error={error}>
      <NumberInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || '0.00'}
        value={(value as number) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        min={field.validation?.min ?? 0}
        max={field.validation?.max}
        decimalScale={field.validation?.precision ?? 2}
        fixedDecimalScale
        prefix={currencySymbol}
        thousandSeparator=","
        onChange={(val) => onChange(val)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function RangeFieldRenderer({ field, value, error, disabled, onChange }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          <FieldLabel field={field} />
          {field.validation?.required && <span style={{ color: 'red' }}> *</span>}
        </Text>
        <Slider
          value={(value as number) ?? field.validation?.min ?? 0}
          min={field.validation?.min ?? 0}
          max={field.validation?.max ?? 100}
          step={field.validation?.step ?? 1}
          disabled={disabled}
          onChange={onChange}
          marks={[
            { value: field.validation?.min ?? 0, label: String(field.validation?.min ?? 0) },
            { value: field.validation?.max ?? 100, label: String(field.validation?.max ?? 100) },
          ]}
          {...(field.props as Record<string, unknown>)}
        />
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
      </Stack>
    </FieldWrapper>
  );
}

// ============================================================================
// DATE/TIME FIELDS
// ============================================================================

function DateFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <DateInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Select date'}
        value={value ? new Date(value as string) : null}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        minDate={field.validation?.minDate ? new Date(field.validation.minDate) : undefined}
        maxDate={field.validation?.maxDate ? new Date(field.validation.maxDate) : undefined}
        onChange={(val) => onChange(val)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function DateTimeFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <DateTimePicker
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Select date and time'}
        value={value ? new Date(value as string) : null}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        minDate={field.validation?.minDate ? new Date(field.validation.minDate) : undefined}
        maxDate={field.validation?.maxDate ? new Date(field.validation.maxDate) : undefined}
        onChange={(val) => onChange(val)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function TimeFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <TimeInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'HH:MM'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

// ============================================================================
// SELECT FIELDS
// ============================================================================

function SelectFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  const data = useMemo(() => toSelectData(field.options || field.dataSource?.options), [field.options, field.dataSource]);

  return (
    <FieldWrapper field={field} error={error}>
      <Select
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Select an option'}
        data={data}
        value={(value as string) ?? null}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        searchable={(field.props?.searchable as boolean) ?? true}
        clearable={(field.props?.clearable as boolean) ?? true}
        onChange={(val) => onChange(val)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function MultiSelectFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  const data = useMemo(() => toSelectData(field.options || field.dataSource?.options), [field.options, field.dataSource]);

  return (
    <FieldWrapper field={field} error={error}>
      <MultiSelect
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Select options'}
        data={data}
        value={(value as string[]) ?? []}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        searchable={(field.props?.searchable as boolean) ?? true}
        clearable={(field.props?.clearable as boolean) ?? true}
        onChange={(val) => onChange(val)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function RadioFieldRenderer({ field, value, error, disabled, onChange }: FieldRendererProps) {
  const options = field.options || field.dataSource?.options || [];

  return (
    <FieldWrapper field={field} error={error}>
      <Radio.Group
        label={<FieldLabel field={field} />}
        value={(value as string) ?? ''}
        error={error}
        required={field.validation?.required}
        onChange={onChange}
        {...(field.props as Record<string, unknown>)}
      >
        <Stack gap="xs" mt="xs">
          {options.map((opt) => (
            <Radio
              key={String(opt.value)}
              value={String(opt.value)}
              label={opt.label}
              disabled={disabled || opt.disabled}
              description={opt.description}
            />
          ))}
        </Stack>
      </Radio.Group>
    </FieldWrapper>
  );
}

// ============================================================================
// BOOLEAN FIELDS
// ============================================================================

function CheckboxFieldRenderer({ field, value, error, disabled, onChange }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <Checkbox
        label={field.label}
        description={field.description}
        checked={Boolean(value)}
        error={error}
        disabled={disabled}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.checked)}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function SwitchFieldRenderer({ field, value, error, disabled, onChange }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <Switch
        label={field.label}
        description={field.description}
        checked={Boolean(value)}
        error={error}
        disabled={disabled}
        required={field.validation?.required}
        onChange={(e) => onChange(e.currentTarget.checked)}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

// ============================================================================
// TEXT AREA FIELDS
// ============================================================================

function TextareaFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <Textarea
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        minLength={field.validation?.minLength}
        maxLength={field.validation?.maxLength}
        minRows={(field.props?.minRows as number) ?? 3}
        maxRows={(field.props?.maxRows as number) ?? 10}
        autosize={(field.props?.autosize as boolean) ?? true}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function JsonFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  const stringValue = useMemo(() => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return JSON.stringify(value, null, 2);
  }, [value]);

  return (
    <FieldWrapper field={field} error={error}>
      <JsonInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || '{}'}
        value={stringValue}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        minRows={(field.props?.minRows as number) ?? 4}
        maxRows={(field.props?.maxRows as number) ?? 20}
        autosize
        formatOnBlur
        validationError="Invalid JSON"
        onChange={(val) => {
          try {
            onChange(val ? JSON.parse(val) : null);
          } catch {
            onChange(val);
          }
        }}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function CodeFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  // For now, use Textarea with monospace font
  // TODO: Integrate Monaco editor or similar for syntax highlighting
  return (
    <FieldWrapper field={field} error={error}>
      <Textarea
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || '// Enter code here'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        minRows={(field.props?.minRows as number) ?? 6}
        maxRows={(field.props?.maxRows as number) ?? 30}
        autosize
        styles={{
          input: {
            fontFamily: 'monospace',
            fontSize: '0.875rem',
          },
        }}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

// ============================================================================
// FILE FIELDS
// ============================================================================

function FileFieldRenderer({ field, value, error, disabled, onChange }: FieldRendererProps) {
  const accept = (field.props?.accept as string) || undefined;

  return (
    <FieldWrapper field={field} error={error}>
      <FileInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Upload file'}
        value={value as File | null}
        error={error}
        disabled={disabled}
        required={field.validation?.required}
        accept={accept}
        leftSection={<IconUpload size={14} />}
        onChange={(file) => onChange(file)}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function ImageFieldRenderer({ field, value, error, disabled, onChange }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <FileInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Upload image'}
        value={value as File | null}
        error={error}
        disabled={disabled}
        required={field.validation?.required}
        accept="image/*"
        leftSection={<IconUpload size={14} />}
        onChange={(file) => onChange(file)}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

// ============================================================================
// SPECIAL FIELDS
// ============================================================================

function ColorFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <ColorInput
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || '#000000'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        format={(field.props?.format as 'hex' | 'rgb' | 'rgba' | 'hsl' | 'hsla') ?? 'hex'}
        swatches={(field.props?.swatches as string[]) ?? undefined}
        onChange={onChange}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function RatingFieldRenderer({ field, value, error, disabled, readOnly, onChange }: FieldRendererProps) {
  return (
    <FieldWrapper field={field} error={error}>
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          <FieldLabel field={field} />
          {field.validation?.required && <span style={{ color: 'red' }}> *</span>}
        </Text>
        <Rating
          value={(value as number) ?? 0}
          count={(field.props?.count as number) ?? 5}
          fractions={(field.props?.fractions as number) ?? 1}
          readOnly={disabled || readOnly}
          onChange={onChange}
          {...(field.props as Record<string, unknown>)}
        />
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
      </Stack>
    </FieldWrapper>
  );
}

function SignatureFieldRenderer({ field, value, error, disabled }: FieldRendererProps) {
  // TODO: Implement canvas-based signature capture
  return (
    <FieldWrapper field={field} error={error}>
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          <FieldLabel field={field} />
          {field.validation?.required && <span style={{ color: 'red' }}> *</span>}
        </Text>
        <Box
          style={{
            border: '1px dashed var(--mantine-color-gray-4)',
            borderRadius: 'var(--mantine-radius-sm)',
            padding: 'var(--mantine-spacing-xl)',
            textAlign: 'center',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {value ? (
            <img src={value as string} alt="Signature" style={{ maxWidth: '100%' }} />
          ) : (
            <Text c="dimmed" size="sm">
              Signature capture (coming soon)
            </Text>
          )}
        </Box>
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
      </Stack>
    </FieldWrapper>
  );
}

function RichTextFieldRenderer({ field, value, error, disabled, onChange, onBlur }: FieldRendererProps) {
  // TODO: Integrate Tiptap or similar rich text editor
  return (
    <FieldWrapper field={field} error={error}>
      <Textarea
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Enter formatted text...'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        required={field.validation?.required}
        minRows={(field.props?.minRows as number) ?? 4}
        autosize
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        description="Rich text editor coming soon"
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function AddressFieldRenderer({ field, value, error, disabled, readOnly, onChange, onBlur }: FieldRendererProps) {
  // TODO: Implement compound address field with autocomplete
  return (
    <FieldWrapper field={field} error={error}>
      <Textarea
        label={<FieldLabel field={field} />}
        placeholder={field.placeholder || 'Enter address'}
        value={(value as string) ?? ''}
        error={error}
        disabled={disabled}
        readOnly={readOnly}
        required={field.validation?.required}
        minRows={3}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        {...(field.props as Record<string, unknown>)}
      />
    </FieldWrapper>
  );
}

function CustomFieldRenderer({ field, error, context }: FieldRendererProps) {
  // Check for custom renderer in context
  const CustomComponent = context.customRenderers?.[field.type];

  if (CustomComponent) {
    return <CustomComponent {...arguments[0]} />;
  }

  return (
    <FieldWrapper field={field} error={error}>
      <Box
        style={{
          border: '1px dashed var(--mantine-color-orange-4)',
          borderRadius: 'var(--mantine-radius-sm)',
          padding: 'var(--mantine-spacing-md)',
          backgroundColor: 'var(--mantine-color-orange-0)',
        }}
      >
        <Text size="sm" c="orange.7">
          Custom field type: {field.type}
        </Text>
        <Text size="xs" c="dimmed">
          No custom renderer registered for this field type.
        </Text>
      </Box>
    </FieldWrapper>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export default FieldRenderer;

// Export individual renderers for direct use
export {
  TextFieldRenderer,
  NumberFieldRenderer,
  EmailFieldRenderer,
  PasswordFieldRenderer,
  PhoneFieldRenderer,
  UrlFieldRenderer,
  DateFieldRenderer,
  DateTimeFieldRenderer,
  TimeFieldRenderer,
  SelectFieldRenderer,
  MultiSelectFieldRenderer,
  RadioFieldRenderer,
  CheckboxFieldRenderer,
  SwitchFieldRenderer,
  TextareaFieldRenderer,
  JsonFieldRenderer,
  CodeFieldRenderer,
  FileFieldRenderer,
  ImageFieldRenderer,
  ColorFieldRenderer,
  RangeFieldRenderer,
  RatingFieldRenderer,
  SignatureFieldRenderer,
  RichTextFieldRenderer,
  AddressFieldRenderer,
  CurrencyFieldRenderer,
  CustomFieldRenderer,
};
